import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

/**
 * Cart drawer upsell carousel: prev/next arrows and one-click add to cart.
 * Also handles the "Add to cart" button on product cards ([data-card-add]).
 * Also makes the drawer always open scrolled to the top.
 *
 * Adding mirrors what the product form does (CartLinesUpdateEvent + Section Rendering API),
 * so the drawer list, totals, header cart count and the carousel itself all refresh the same way.
 * Listeners live on the document, so they keep working after the drawer re-renders.
 */
document.addEventListener('click', (event) => {
  const target = event.target instanceof Element ? event.target : null;
  if (!target) return;

  const nav = target.closest('[data-upsell-nav]');
  if (nav) {
    const track = nav.closest('[data-cart-upsell]')?.querySelector('[data-upsell-track]');
    if (!track) return;
    const direction = nav.getAttribute('data-upsell-nav') === 'next' ? 1 : -1;
    const card = track.querySelector('.cart-upsell__card');
    const step = (card ? card.offsetWidth : track.clientWidth) + 12;
    const max = track.scrollWidth - track.clientWidth;
    // Wrap around so the arrows always do something
    if (direction > 0 && track.scrollLeft >= max - 4) {
      track.scrollTo({ left: 0, behavior: 'smooth' });
    } else if (direction < 0 && track.scrollLeft <= 4) {
      track.scrollTo({ left: max, behavior: 'smooth' });
    } else {
      track.scrollBy({ left: direction * step, behavior: 'smooth' });
    }
    return;
  }

  const add = target.closest('[data-upsell-add], [data-card-add]');
  if (add instanceof HTMLButtonElement) {
    if (add.hasAttribute('data-card-add')) {
      // Keep the click from also opening the product page
      event.preventDefault();
      event.stopPropagation();
    }
    addToCart(add);
  }
});

/* ---- Always open the drawer scrolled to the top ---- */

function resetDrawerScroll() {
  document
    .querySelectorAll('#cart-drawer .cart-drawer__items, #cart-drawer .cart-drawer__content')
    .forEach((el) => {
      el.scrollTop = 0;
    });
  document.querySelectorAll('#cart-drawer [data-upsell-track]').forEach((el) => {
    el.scrollLeft = 0;
  });
}

function watchDrawer() {
  const dialog = document.querySelector('#cart-drawer dialog');
  if (!(dialog instanceof HTMLDialogElement) || dialog.dataset.scrollWatch) return;
  dialog.dataset.scrollWatch = 'true';

  let wasOpen = dialog.open;

  const onOpen = () => {
    resetDrawerScroll();
    requestAnimationFrame(resetDrawerScroll);
    // once more after the open animation has settled, unless the drawer was closed again meanwhile
    setTimeout(() => {
      if (dialog.open) resetDrawerScroll();
    }, 350);
  };

  // Only react to a real closed -> open change. Cart re-renders can touch the dialog's
  // attributes while it stays open, and those must not reset the scroll position.
  new MutationObserver(() => {
    const isOpen = dialog.open;
    if (isOpen && !wasOpen) onOpen();
    wasOpen = isOpen;
  }).observe(dialog, { attributes: true, attributeFilter: ['open'] });

  if (dialog.open) onOpen();
}

watchDrawer();
document.addEventListener('DOMContentLoaded', watchDrawer);
// The drawer markup can arrive after this module runs, so also try on the first interactions
document.addEventListener('pointerdown', watchDrawer, { capture: true });
document.addEventListener('keydown', watchDrawer, { capture: true });

/**
 * While the cart re-renders, the list briefly gets shorter (the add-on card leaves, the new row
 * animates in from zero height) and the browser clamps the scroll position to the top.
 * Hold the position the shopper had until the list has settled, unless they scroll themselves.
 * @param {number} saved
 */
function keepListScroll(saved) {
  if (!saved) return;

  const controller = new AbortController();
  const { signal } = controller;
  for (const type of ['wheel', 'touchstart', 'pointerdown', 'keydown']) {
    document.addEventListener(type, () => controller.abort(), { once: true, capture: true, passive: true, signal });
  }

  const end = performance.now() + 2500;
  const tick = () => {
    if (signal.aborted || performance.now() > end) {
      controller.abort();
      return;
    }
    const list = document.querySelector('#cart-drawer .cart-drawer__items');
    if (list && Math.abs(list.scrollTop - saved) > 1) list.scrollTop = saved;
    requestAnimationFrame(tick);
  };
  requestAnimationFrame(tick);
}

/** @param {HTMLButtonElement} button */
async function addToCart(button) {
  if (button.disabled) return;

  const variantId = button.dataset.variantId;
  if (!variantId) return;

  const isCard = button.hasAttribute('data-card-add');
  const label = button.textContent;
  button.disabled = true;
  button.textContent = 'Adding...';

  keepListScroll(button.closest('.cart-drawer__items')?.scrollTop ?? 0);

  const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
    .map((el) => (el instanceof HTMLElement ? el.dataset.sectionId : undefined))
    .filter(Boolean);

  const deferred = CartLinesUpdateEvent.createPromise();

  button.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: [{ merchandiseId: variantId, quantity: 1 }],
      promise: deferred.promise,
    })
  );

  try {
    const response = await fetch(Theme.routes.cart_add_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        id: Number(variantId),
        quantity: 1,
        sections: sectionIds.join(','),
        sections_url: window.location.pathname,
      }),
    });
    const data = await response.json();
    if (data.status) throw new Error(data.description || data.message || 'Add to cart failed');

    const cart = await fetch(`${Theme.routes.cart_url}.json`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    }).then((r) => r.json());

    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {
        items: cart.items,
        source: isCard ? 'product-card' : 'cart-upsell',
        sourceId: isCard ? 'product-card' : 'cart-upsell',
        itemCount: 1,
        productId: button.dataset.productId,
        sections: data.sections,
        didError: false,
      },
    });

    if (isCard) {
      // Short confirmation on the card itself, then back to the normal button
      button.textContent = 'Added ✓';
      button.classList.add('is-added');
      setTimeout(() => {
        button.textContent = label;
        button.classList.remove('is-added');
        button.disabled = false;
      }, 2000);
    }
  } catch (error) {
    deferred.reject(error);
    button.dispatchEvent(
      new CartErrorEvent({
        error: error instanceof Error ? error.message : 'Add to cart failed',
        code: 'INVALID',
      })
    );
    button.textContent = 'Try again';
    setTimeout(() => {
      button.textContent = label;
      button.disabled = false;
    }, 2000);
  }
}
